import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { selectEmail } from "../Redux/authSlice";
import { useUsage } from "./UsageContext";
import UpgradeDialog from "../component/Upgrade/UpgradeDialog";
import {
  PRO_FEATURES,
  TRIAL_WELCOME_WINDOW_DAYS,
  UPGRADE_PROMPT_IDS as IDS,
  checkMinutesBack,
  claimPrompt,
  fmtDay,
  freeFractionLeft,
  promptsLockQuiet,
  recentlyEndedTrial,
  recordPromptsLockDecline,
  rememberTrial,
  retirePrompt,
  trackPromptClicked,
  trackPromptShown,
  trialBannerCopy,
  trialBannerStage,
  trialEpisode,
  weekEpisode,
} from "../lib/upgradeNudges";

// Nudges only appear inside the app itself, never on marketing pages.
// /settings is the upgrade destination, so nothing is pushed there either.
const NUDGE_ROUTES = ["/app", "/usage"];
// Home settles before a dialog opens, as on Android.
const DIALOG_DELAY_MS = 800;
const TOAST_MS = 8000;

const UpgradeNudgesContext = createContext(null);

const NudgeToast = ({ title, body, cta, onCta }) => (
  <div>
    <div style={{ fontWeight: 600 }}>{title}</div>
    {body && <div style={{ marginTop: 4, opacity: 0.9 }}>{body}</div>}
    {cta && (
      <button
        type="button"
        onClick={onCta}
        style={{
          marginTop: 8,
          padding: "6px 14px",
          borderRadius: 999,
          background: "linear-gradient(90deg, #3AB1F6, #407BFF)",
          color: "#fff",
          fontWeight: 600,
          fontSize: 13,
        }}
      >
        {cta}
      </button>
    )}
  </div>
);

export const UpgradeNudgesProvider = ({ children }) => {
  const { usage, bannerState } = useUsage();
  const scope = useSelector(selectEmail);
  const location = useLocation();
  const navigate = useNavigate();
  const onNudgeRoute = NUDGE_ROUTES.includes(location.pathname);

  const [dialog, setDialog] = useState(null);
  // One dialog per page load, like Android's one Home dialog per launch.
  const dialogUsedRef = useRef(false);

  const openUpgrade = useCallback(
    (promptId, surface, action = "upgrade") => {
      trackPromptClicked(promptId, surface, action);
      navigate("/settings", { state: { upgradeSource: promptId } });
    },
    [navigate]
  );

  const showToast = useCallback(
    (promptId, { title, body, cta }) => {
      const id = `upgrade-${promptId}`;
      toast.info(
        <NudgeToast
          title={title}
          body={body}
          cta={cta}
          onCta={() => {
            toast.dismiss(id);
            openUpgrade(promptId, "toast");
          }}
        />,
        { toastId: id, autoClose: TOAST_MS }
      );
    },
    [openUpgrade]
  );

  const openDialog = useCallback((next) => {
    if (dialogUsedRef.current) return false;
    dialogUsedRef.current = true;
    setTimeout(() => setDialog(next), DIALOG_DELAY_MS);
    return true;
  }, []);

  /* ------------------------------ trial journey ------------------------------ */
  useEffect(() => {
    if (!scope || !usage || !onNudgeRoute) return;

    if (usage.isTrial) {
      rememberTrial(scope, usage);
      const episode = trialEpisode(usage);
      const daysIn =
        usage.trialLengthDays != null && usage.trialDaysLeft != null
          ? usage.trialLengthDays - usage.trialDaysLeft
          : null;
      if (
        daysIn != null &&
        daysIn <= TRIAL_WELCOME_WINDOW_DAYS &&
        !dialogUsedRef.current &&
        claimPrompt(scope, IDS.trialWelcome, episode, "dialog")
      ) {
        openDialog({ kind: IDS.trialWelcome, endsOn: usage.trialEndsOn });
      }
      return;
    }

    const ended = recentlyEndedTrial(scope, usage);
    if (
      ended &&
      !dialogUsedRef.current &&
      claimPrompt(scope, IDS.trialEnded, ended.toISOString().slice(0, 10), "dialog")
    ) {
      openDialog({ kind: IDS.trialEnded, totalMin: Math.round(usage.totalMin || 0) });
    }
  }, [scope, usage, onNudgeRoute, openDialog]);

  /* ---------------------------- free weekly minutes ---------------------------- */
  useEffect(() => {
    if (!scope || !usage || usage.isPro || usage.isTrial || !onNudgeRoute) return;
    const episode = weekEpisode(usage);
    const totalMin = Math.round(usage.totalMin || 0);

    const back = checkMinutesBack(scope, usage);
    if (back && claimPrompt(scope, IDS.freeReset, episode, "toast")) {
      showToast(IDS.freeReset, {
        title: "Your free minutes are back",
        body: `${totalMin} minutes are ready for this week. Last week you dictated ${back.lastWeekUsedMin} minutes.`,
        cta: "Go unlimited",
      });
      return;
    }

    const left = freeFractionLeft(usage);
    if (left == null) return;
    if (left <= 0.2) {
      // A long dictation jumped past halfway; the amber minutes pill covers it now.
      retirePrompt(scope, IDS.free50, episode);
    } else if (left <= 0.5 && claimPrompt(scope, IDS.free50, episode, "toast")) {
      const minLeft = Math.max(0, Math.floor(usage.remainingMin || 0));
      showToast(IDS.free50, {
        title: "Half your free minutes are used",
        body: `${minLeft} min left this week. Pro has no weekly limit.`,
        cta: "Upgrade",
      });
    }
  }, [scope, usage, onNudgeRoute, showToast]);

  /* ------------------------ top bar minutes "shown" log ------------------------ */
  // The minutes pill is always on for Free accounts. It counts as the 20% and
  // the exhausted prompt once 80% of the week is used, logged once per week.
  const freeBannerPromptId = bannerState
    ? bannerState.severity === "limit-reached"
      ? IDS.freeExhausted
      : IDS.free20
    : null;

  useEffect(() => {
    if (!scope || !freeBannerPromptId || !onNudgeRoute) return;
    claimPrompt(scope, freeBannerPromptId, weekEpisode(usage), "top_bar");
  }, [scope, freeBannerPromptId, onNudgeRoute, usage]);

  /* -------------------------------- trial pill -------------------------------- */
  const trialBanner = useMemo(() => {
    const stage = trialBannerStage(usage);
    if (!stage) return null;
    const episode = trialEpisode(usage);
    const copy = trialBannerCopy(stage, usage);
    return copy ? { ...copy, stage, episode, urgent: stage !== IDS.trialMidpoint } : null;
  }, [usage]);

  useEffect(() => {
    if (!scope || !trialBanner || !onNudgeRoute) return;
    claimPrompt(scope, trialBanner.stage, trialBanner.episode, "top_bar");
  }, [scope, trialBanner, onNudgeRoute]);

  /* ------------------------------ custom prompts ------------------------------ */
  const openPromptsLock = useCallback(
    (surface = "prompts_panel") => {
      if (promptsLockQuiet()) {
        trackPromptShown(IDS.promptsLocked, "toast");
        showToast(IDS.promptsLocked, {
          title: "Custom prompts are part of Pro",
          cta: "See Pro plans",
        });
        return;
      }
      trackPromptShown(IDS.promptsLocked, surface);
      setDialog({ kind: IDS.promptsLocked, surface });
    },
    [showToast]
  );

  /* --------------------------------- dialogs --------------------------------- */
  const closeDialog = useCallback(() => setDialog(null), []);

  const dialogProps = useMemo(() => {
    if (!dialog) return null;
    switch (dialog.kind) {
      case IDS.trialWelcome: {
        const end = fmtDay(dialog.endsOn);
        return {
          icon: "crown",
          title: "Enjoy Zero Typing Pro",
          body: end
            ? `Every Pro feature is unlocked until ${end}.`
            : "Every Pro feature is unlocked during your trial.",
          bullets: PRO_FEATURES,
          primaryLabel: "Start using Pro",
          secondaryLabel: "See Pro plans",
          onPrimary: () => {
            trackPromptClicked(IDS.trialWelcome, "dialog", "dismiss");
            closeDialog();
          },
          onSecondary: () => {
            closeDialog();
            openUpgrade(IDS.trialWelcome, "dialog", "see_plans");
          },
          onClose: () => {
            trackPromptClicked(IDS.trialWelcome, "dialog", "dismiss");
            closeDialog();
          },
        };
      }
      case IDS.trialEnded:
        return {
          tone: "warn",
          icon: "hourglass",
          title: "Your Pro trial has ended",
          body: `You're now on the Free plan, with ${dialog.totalMin || 15} minutes of dictation each week. Upgrade to keep unlimited dictation and custom prompts.`,
          primaryLabel: "Upgrade to Pro",
          secondaryLabel: "Continue with Free",
          onPrimary: () => {
            closeDialog();
            openUpgrade(IDS.trialEnded, "dialog");
          },
          onSecondary: () => {
            trackPromptClicked(IDS.trialEnded, "dialog", "dismiss");
            closeDialog();
          },
        };
      case IDS.promptsLocked:
        return {
          icon: "sparkles",
          title: "Custom prompts are part of Pro",
          body: "Write an instruction once and apply it to any dictation.",
          example: (
            <>
              <span className="font-semibold text-[#0F172A]">
                "Turn this into a standup update"
              </span>
              <br />
              Yesterday: shipped the export fix
              <br />
              Today: billing review with Priya
              <br />
              Blockers: none
            </>
          ),
          primaryLabel: "See Pro plans",
          secondaryLabel: "Not now",
          onPrimary: () => {
            closeDialog();
            openUpgrade(IDS.promptsLocked, dialog.surface, "see_plans");
          },
          onSecondary: () => {
            recordPromptsLockDecline();
            trackPromptClicked(IDS.promptsLocked, dialog.surface, "dismiss");
            closeDialog();
          },
        };
      default:
        return null;
    }
  }, [dialog, closeDialog, openUpgrade]);

  const value = useMemo(
    () => ({
      openUpgrade,
      openPromptsLock,
      trialBanner,
      freeBannerPromptId,
    }),
    [openUpgrade, openPromptsLock, trialBanner, freeBannerPromptId]
  );

  return (
    <UpgradeNudgesContext.Provider value={value}>
      {children}
      <UpgradeDialog open={Boolean(dialogProps)} {...(dialogProps || {})} />
    </UpgradeNudgesContext.Provider>
  );
};

export const useUpgradeNudges = () => {
  const ctx = useContext(UpgradeNudgesContext);
  if (!ctx) {
    throw new Error("useUpgradeNudges must be used within an <UpgradeNudgesProvider>");
  }
  return ctx;
};
